import React, { useState } from 'react';
import { NavLink, useNavigate, useLocation } from 'react-router-dom';
import { useSession } from '@/lib/session';
import { ROUTE_FEATURE_MAP } from '@/lib/nav.config';
import { visibleBottomTabs } from '@/admin/selectors';
import { useAdminStore } from '@/admin/adminStore';
import { Icon } from '@/components/ui/Icon';
import { Modal } from '@/components/ui/Modal';
import styles from './BottomTabBar.module.css';

export function BottomTabBar() {
  const { role: sessionRole } = useSession();
  const store = useAdminStore();
  const [isMoreOpen, setIsMoreOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  const role = (sessionRole === 'student' || sessionRole === 'parent')
    ? sessionRole
    : (location.pathname.startsWith('/student') ? 'student' : location.pathname.startsWith('/parent') ? 'parent' : null);

  if (!role) {
    return null;
  }

  const items = visibleBottomTabs(role, store);
  if (items.length === 0) return null;

  const rawMoreItems = role === 'student' ? [
    { to: '/student/fees', label: 'Fees & Dues', icon: 'receipt' },
    { to: '/student/timetable', label: 'Timetable', icon: 'clock' },
    { to: '/student/notices', label: 'Notices', icon: 'bell' },
    { to: '/student/chat', label: 'Class Chat', icon: 'chat' },
    { to: '/student/profile', label: 'My Profile', icon: 'user' },
    { to: '/student/notifications', label: 'Alerts', icon: 'bell' }
  ] : [
    { to: '/parent/homework', label: 'Homework', icon: 'book' },
    { to: '/parent/timetable', label: 'Timetable', icon: 'clock' },
    { to: '/parent/notices', label: 'Notices', icon: 'bell' },
    { to: '/parent/chat', label: 'Chat', icon: 'chat' },
    { to: '/parent/profile', label: 'Profile', icon: 'user' },
    { to: '/parent/notifications', label: 'Alerts', icon: 'bell' }
  ];

  const hiddenList = store?.hiddenMenus?.[role] || [];
  const moreItems = rawMoreItems.filter((item) => {
    if (hiddenList.includes(item.to)) return false;
    const featId = ROUTE_FEATURE_MAP[item.to];
    if (featId && store?.features?.[featId]?.enabled === false) return false;
    return true;
  });

  return (
    <>
      <nav className={styles.bottomBar} aria-label="Quick Access Tabs">
        {items.map((item) => {
          if (item.isMore) {
            return (
              <button
                key="more-btn"
                type="button"
                className={`${styles.tabItem} ${styles.moreTabBtn} ${isMoreOpen ? styles.tabActive : ''}`}
                onClick={() => setIsMoreOpen((prev) => !prev)}
                aria-label="More options"
                aria-expanded={isMoreOpen}
              >
                <Icon name={item.icon || 'menu'} size={20} className={styles.tabIcon} />
                <span className={styles.tabLabel}>{item.label}</span>
              </button>
            );
          }

          return (
            <NavLink
              key={item.to}
              to={item.to}
              onClick={() => setIsMoreOpen(false)}
              className={({ isActive }) =>
                `${styles.tabItem} ${isActive && !isMoreOpen ? styles.tabActive : ''}`
              }
            >
              {item.icon && <Icon name={item.icon} size={20} className={styles.tabIcon} />}
              <span className={styles.tabLabel}>{item.label}</span>
            </NavLink>
          );
        })}
      </nav>

      {/* Slide-up Sheet for 'More' */}
      <Modal
        isOpen={isMoreOpen}
        onClose={() => setIsMoreOpen(false)}
        title="More Services"
        subtitle="Quick access to student portal features"
        aboveBottomBar
      >
        <div className={styles.moreGrid}>
          {moreItems.map((m) => (
            <button
              key={m.to}
              type="button"
              className={styles.moreTile}
              onClick={() => {
                setIsMoreOpen(false);
                navigate(m.to);
              }}
            >
              <div className={styles.moreTileIcon}>
                <Icon name={m.icon} size={20} />
              </div>
              <span className={styles.moreTileLabel}>{m.label}</span>
            </button>
          ))}
        </div>
      </Modal>
    </>
  );
}

